import type { PurchaseLineItem } from '../types/purchasing';

export function emptyPurchaseLineDraft(): PurchaseLineDraft {
  return {
    key: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    description: '',
    quantity: '1',
    unitPrice: '',
    taxable: true,
  };
}

export interface PurchaseLineDraft {
  key: string;
  description: string;
  quantity: string;
  unitPrice: string;
  taxable: boolean;
  /** Per-line sales-tax percent. When set, this line does not use the PR rate. */
  taxRate?: number;
  /** Dollar tax stored for this line. Wins over taxRate. */
  taxAmount?: number;
  /** Kept so a later edit of a designer-invoice PO does not drop imported hours. */
  hours?: number;
  productUrl?: string;
  productLabel?: string;
  vendorName?: string;
  poNumber?: string;
}

export function roundMoney(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100) / 100;
}

export function lineListedAmount(quantity: number, unitPrice: number): number {
  if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return 0;
  return roundMoney(quantity * unitPrice);
}

export function lineTaxAmount(listed: number, salesTaxPct: number, taxable = true): number {
  if (!taxable || !Number.isFinite(listed) || !Number.isFinite(salesTaxPct) || salesTaxPct <= 0) {
    return 0;
  }
  return roundMoney(listed * (salesTaxPct / 100));
}

type LineTaxInput = {
  quantity: number;
  unitPrice: number;
  taxable?: boolean;
  taxRate?: number;
  taxAmount?: number;
};

/** Listed, tax, and amount. A line taxRate uses that line's tax and ignores the PR rate. */
export function lineFigures(item: LineTaxInput, salesTaxPct = 0): {
  listed: number;
  tax: number;
  amount: number;
} {
  const listed = lineListedAmount(item.quantity, item.unitPrice);
  if (typeof item.taxRate === 'number' && Number.isFinite(item.taxRate)) {
    const tax =
      typeof item.taxAmount === 'number' && Number.isFinite(item.taxAmount)
        ? roundMoney(item.taxAmount)
        : lineTaxAmount(listed, item.taxRate, true);
    return { listed, tax, amount: roundMoney(listed + tax) };
  }
  const tax = lineTaxAmount(listed, salesTaxPct, item.taxable !== false);
  return { listed, tax, amount: roundMoney(listed + tax) };
}

/** Qty × listed, plus sales tax when the line is taxable. */
export function lineItemAmount(
  quantity: number,
  unitPrice: number,
  salesTaxPct = 0,
  taxable = true,
): number {
  const listed = lineListedAmount(quantity, unitPrice);
  return roundMoney(listed + lineTaxAmount(listed, salesTaxPct, taxable));
}

function isLegacyLine(row: Record<string, unknown>): boolean {
  return row.taxable === undefined && row.taxAmount === undefined;
}

export function parsePurchaseLineItems(
  raw: unknown,
  fallback?: { title?: string; amount?: number },
): PurchaseLineItem[] {
  if (Array.isArray(raw)) {
    const items = raw
      .map((item, index): PurchaseLineItem | null => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Record<string, unknown>;
        const description = typeof row.description === 'string' ? row.description.trim() : '';
        const quantity = Number(row.quantity);
        const unitPrice = Number(row.unitPrice);
        if (!description && !(Number.isFinite(unitPrice) && unitPrice > 0)) return null;
        const taxable = isLegacyLine(row) ? false : row.taxable !== false;
        const taxRate = Number(row.taxRate);
        const taxAmount = Number(row.taxAmount);
        const hasTaxRate = row.taxRate != null && row.taxRate !== '' && Number.isFinite(taxRate);
        const hasTaxAmount = row.taxAmount != null && row.taxAmount !== '' && Number.isFinite(taxAmount);
        const hours = Number(row.hours);
        const productUrl = typeof row.productUrl === 'string' ? row.productUrl.trim() : '';
        const productLabel = typeof row.productLabel === 'string' ? row.productLabel.trim() : '';
        const vendorName = typeof row.vendorName === 'string' ? row.vendorName.trim() : '';
        const poNumber = typeof row.poNumber === 'string' ? row.poNumber.trim() : '';
        return {
          id: typeof row.id === 'string' && row.id.trim() ? row.id.trim() : `line-${index + 1}`,
          description: description || `Item ${index + 1}`,
          quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
          unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
          taxable,
          ...(hasTaxRate ? { taxRate } : {}),
          ...(hasTaxAmount ? { taxAmount } : {}),
          ...(Number.isFinite(hours) && hours > 0 ? { hours } : {}),
          ...(productUrl ? { productUrl, ...(productLabel ? { productLabel } : {}) } : {}),
          ...(vendorName ? { vendorName } : {}),
          ...(poNumber ? { poNumber } : {}),
        };
      })
      .filter((item): item is PurchaseLineItem => !!item);
    if (items.length) return items;
  }

  const title = fallback?.title?.trim();
  const amount = Number(fallback?.amount);
  if (title || (Number.isFinite(amount) && amount > 0)) {
    return [
      {
        id: 'line-1',
        description: title || 'Purchase',
        quantity: 1,
        unitPrice: Number.isFinite(amount) ? amount : 0,
        taxable: false,
      },
    ];
  }
  return [];
}

export function requisitionListedTotal(
  items: Array<Pick<PurchaseLineItem, 'quantity' | 'unitPrice'>>,
): number {
  return roundMoney(items.reduce((sum, item) => sum + lineListedAmount(item.quantity, item.unitPrice), 0));
}

export function requisitionTaxTotal(
  items: Array<Pick<PurchaseLineItem, 'quantity' | 'unitPrice' | 'taxable' | 'taxRate' | 'taxAmount'>>,
  salesTaxPct: number,
): number {
  return roundMoney(items.reduce((sum, item) => sum + lineFigures(item, salesTaxPct).tax, 0));
}

export function requisitionTotal(
  items: Array<Pick<PurchaseLineItem, 'quantity' | 'unitPrice' | 'taxable' | 'taxRate' | 'taxAmount'>>,
  salesTaxPct = 0,
): number {
  return roundMoney(items.reduce((sum, item) => sum + lineFigures(item, salesTaxPct).amount, 0));
}

export function summarizeRequisitionTitle(items: Array<Pick<PurchaseLineItem, 'description'>>): string {
  const names = items.map(item => item.description.trim()).filter(Boolean);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names[0]} + ${names.length - 1} more`;
}

export function draftsToLineItems(drafts: PurchaseLineDraft[]): PurchaseLineItem[] {
  return drafts
    .map((draft, index): PurchaseLineItem | null => {
      const description = draft.description.trim();
      const quantity = Number(draft.quantity);
      const unitPrice = Number(draft.unitPrice);
      if (!description && !(Number.isFinite(unitPrice) && unitPrice > 0)) return null;
      if (!description) return null;
      const hours = Number(draft.hours);
      const productUrl = draft.productUrl?.trim() || '';
      const productLabel = draft.productLabel?.trim() || '';
      const vendorName = draft.vendorName?.trim() || '';
      const poNumber = draft.poNumber?.trim() || '';
      return {
        id: draft.key || `line-${index + 1}`,
        description,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
        taxable: draft.taxable !== false,
        ...(typeof draft.taxRate === 'number' ? { taxRate: draft.taxRate } : {}),
        ...(typeof draft.taxAmount === 'number' ? { taxAmount: draft.taxAmount } : {}),
        ...(Number.isFinite(hours) && hours > 0 ? { hours } : {}),
        ...(productUrl ? { productUrl, ...(productLabel ? { productLabel } : {}) } : {}),
        ...(vendorName ? { vendorName } : {}),
        ...(poNumber ? { poNumber } : {}),
      };
    })
    .filter((item): item is PurchaseLineItem => !!item);
}

export function lineItemsToDrafts(items: PurchaseLineItem[]): PurchaseLineDraft[] {
  if (!items.length) return [emptyPurchaseLineDraft()];
  return items.map(item => ({
    key: item.id,
    description: item.description,
    quantity: String(item.quantity),
    unitPrice: item.unitPrice === 0 ? '' : String(item.unitPrice),
    taxable: item.taxable !== false,
    ...(typeof item.taxRate === 'number' ? { taxRate: item.taxRate } : {}),
    ...(typeof item.taxAmount === 'number' ? { taxAmount: item.taxAmount } : {}),
    ...(typeof item.hours === 'number' && item.hours > 0 ? { hours: item.hours } : {}),
    ...(item.productUrl ? { productUrl: item.productUrl, ...(item.productLabel ? { productLabel: item.productLabel } : {}) } : {}),
    ...(item.vendorName ? { vendorName: item.vendorName } : {}),
    ...(item.poNumber ? { poNumber: item.poNumber } : {}),
  }));
}

export function serializePurchaseLineItems(items: PurchaseLineItem[], salesTaxPct = 0) {
  return items.map(item => {
    const listedAmount = lineListedAmount(item.quantity, item.unitPrice);
    const figures = lineFigures(item, salesTaxPct);
    const taxAmount = figures.tax;
    return {
      id: item.id,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      taxable: item.taxable,
      ...(typeof item.taxRate === 'number' ? { taxRate: item.taxRate } : {}),
      listedAmount,
      taxAmount,
      amount: figures.amount,
      ...(typeof item.hours === 'number' && item.hours > 0 ? { hours: item.hours } : {}),
      ...(item.productUrl
        ? { productUrl: item.productUrl, ...(item.productLabel ? { productLabel: item.productLabel } : {}) }
        : {}),
      ...(item.vendorName ? { vendorName: item.vendorName } : {}),
      ...(item.poNumber ? { poNumber: item.poNumber } : {}),
    };
  });
}
